<template>
  <div v-if="hasAnyPermission('smartcat-attachment-view')" :class="usedIn === 'CataloguerForm' ? 'py-2 container-fluid' : 'py-4 container-fluid'">
    <ToastPrime position="bottom-left"/>
    <div class="card">
      <div class="card-header py-3" :class="{ 'px-3': usedIn === 'CataloguerForm' }">
        <div class="row align-items-center">
          <div class="col-4">
            <h5 v-if="usedIn === 'SmartcatAttachment'" class="mb-0">Smartcat Attachments</h5>
            <h6 v-else class="mb-0">Smartcat Attachments</h6>
          </div>
          <div class="col-4 text-end">
            <IconField class="w-100">
              <InputIcon class="pi pi-search" />
              <InputText v-model="table.search" type="text" size="small" placeholder="Search" class="rounded-pill w-100" @keyup="filter"/>
            </IconField>
          </div>
					<div class="col-4 ms-auto">
            <div class="my-auto ms-auto float-end">
              <ButtonPrime v-if="hasAnyPermission('smartcat-attachment-create-folder') && (usedIn === 'SmartcatAttachment' || usedIn === 'CataloguerForm')" v-tooltip.top="'Add Folder'" icon="pi pi-folder-plus" rounded severity="primary" :size="usedIn === 'CataloguerForm' ? 'small' : 'normal'" class="ms-1" :disabled="modal.onLoading" @click="openModalAddFolder"/>
              <ButtonPrime v-if="hasAnyPermission('smartcat-attachment-create-file') && (usedIn === 'SmartcatAttachment' || usedIn === 'CataloguerForm')" v-tooltip.top="'Upload File'" icon="pi pi-file-plus" rounded severity="primary" :size="usedIn === 'CataloguerForm' ? 'small' : 'normal'" class="ms-1" :disabled="modal.onLoading" @click="openModalUploadFile"/>
              <ButtonPrime v-if="hasAnyPermission('smartcat-attachment-create-file') && (usedIn === 'SmartcatAttachment' || usedIn === 'CataloguerForm')" v-tooltip.top="'Upload Link'" icon="pi pi-link" rounded severity="primary" :size="usedIn === 'CataloguerForm' ? 'small' : 'normal'" class="ms-1" :disabled="modal.onLoading" @click="openModalUploadLink"/>
            </div>
          </div>
        </div>
      </div>

			<div v-if="breadCrmb.items.length > 0" class="pb-3" :class="usedIn === 'CataloguerForm' ? 'px-1' : 'px-2'">
				<PrimeBreadcrumb :home="breadCrmb.home" :model="breadCrmb.items" :class="usedIn === 'CataloguerForm' ? 'py-0 text-xs' : 'py-0'">
					<template #item="{ item, props }">
              <a v-if="item.id && item.folder_name" href="#" v-bind="props.action" @click.prevent="clickCrumb('child', item)">
                <span :class="[item.icon, 'text-color']" />
                <span class="dark:text-surface-0 font-semibold">{{ item.folder_name }}</span>
              </a>
              <a v-else href="#" v-bind="props.action" @click.prevent="clickCrumb('parent', item)">
									<span class="text-surface-700 dark:text-surface-0">{{ item.folder_name }}</span>
							</a>
					</template>
				</PrimeBreadcrumb>
			</div>

      <!-- DataTable -->
      <div class="table-container">
        <DataTable :value="table.onLoad ? table.dummyRows : table.data" :class="usedIn === 'CataloguerForm' ? 'p-datatable-sm text-xs' : 'datatable-sm text-sm'" removable-sort scrollable scroll-height="flex" :table-style="usedIn === 'CataloguerForm' ? 'min-width: 30rem' : 'min-width: 70rem'">
          <Column header="Name" header-class="bg-light text-dark" sortable>
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <div v-else class="d-flex align-items-center item-row" :class="{ 'choosable-item': usedIn === 'CataloguerForm' && (row.data.type === 'file' || row.data.type === 'link') }" @click="handleItemClick(row.data)">
                <div class="cursor-pointer d-flex align-items-center">
                  <i v-if="row.data.type" :class="row.data.type === 'folder' ? 'pi pi-folder text-warning' : (row.data.type === 'file') ? getFileIcon(row.data.file_name) : 'pi pi-globe text-primary'"></i>
                  <span class="ms-3" :class="row.data.type === 'link' ? 'text-primary' : ''">
                    {{ row.data.type === 'folder' ? row.data.folder_name : (row.data.type === 'file') ? row.data.file_name : shortenLink(row.data.file_name) }}
                  </span>
                </div>
                <div v-if="row.data.type" class="action-buttons-hover ms-auto d-flex gap-1" @click.stop>
                  <ButtonPrime v-if="row.data.type === 'file' && (isImage(row.data.file_name) || getFileExtension(row.data.file_name) === 'pdf')" v-tooltip.top="'Preview'" icon="pi pi-eye" outlined rounded severity="info" size="small" @click="handleItemClick(row.data)" />
                  <ButtonPrime v-if="row.data.type != 'folder' && usedIn === 'CataloguerForm'" v-tooltip.top="'Copy Link'" icon="pi pi-globe" outlined rounded severity="info" :size="usedIn === 'CataloguerForm' ? 'small' : 'small'" @click="openModalCopyLink(row.data)" />
                  <ButtonPrime v-if="canAction('rename', row.data.type) && usedIn === 'SmartcatAttachment'" v-tooltip.top="'Rename'" icon="pi pi-pencil" outlined rounded size="small" @click="openModalRenameItem(row.data)" />
                  <ButtonPrime v-if="row.data.type != 'folder' && usedIn === 'SmartcatAttachment'" v-tooltip.top="'Download'" icon="pi pi-download" outlined rounded severity="success" size="small" @click="downloadFile(row.data)" />
                  <ButtonPrime v-if="canAction('delete', row.data.type) && usedIn === 'SmartcatAttachment'" v-tooltip.top="'Delete'" icon="pi pi-trash" outlined rounded severity="danger" size="small" @click="deleteItem(row.data)" />
                  <ButtonPrime v-if="canAction('moveto', row.data.type) && usedIn === 'SmartcatAttachment'" v-tooltip.top="'Move here'" icon="pi pi-file-export" outlined rounded severity="help" size="small" @click="openModalMoveItem(row.data)" />
                  <ButtonPrime v-if="canAction('rename', row.data.type) && usedIn === 'CataloguerForm'" v-tooltip.top="'Rename'" icon="pi pi-pencil" outlined rounded size="small" @click="openModalRenameItem(row.data)" />
                  <ButtonPrime v-if="row.data.type != 'folder' && usedIn === 'CataloguerForm'" v-tooltip.top="'Download'" icon="pi pi-download" outlined rounded severity="success" size="small" @click="downloadFile(row.data)" />
                  <ButtonPrime v-if="canAction('delete', row.data.type) && usedIn === 'CataloguerForm'" v-tooltip.top="'Delete'" icon="pi pi-trash" outlined rounded severity="danger" size="small" @click="deleteItem(row.data)" />
                  <ButtonPrime v-if="canAction('moveto', row.data.type) && usedIn === 'CataloguerForm'" v-tooltip.top="'Move here'" icon="pi pi-file-export" outlined rounded severity="help" size="small" @click="openModalMoveItem(row.data)" />
                </div>
              </div>
            </template>
          </Column>
          <Column v-if="usedIn === 'SmartcatAttachment'" header="Created/Uploaded by" header-class="bg-light text-dark">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <div v-else class="cursor-pointer" @click="handleItemClick(row.data)">
                <span>{{ row.data.type === 'folder' ? row.data.created_by : row.data.uploaded_by }}</span>
              </div>
            </template>
          </Column>
          <Column v-if="usedIn === 'CataloguerForm'" header="Uploaded by" header-class="bg-light text-dark" :style="{ width: '150px' }">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <div v-else class="cursor-pointer" @click="handleItemClick(row.data)">
                <span>{{ row.data.type === 'folder' ? row.data.created_by : row.data.uploaded_by }}</span>
              </div>
            </template>
          </Column>
          <Column v-if="usedIn === 'CataloguerForm'" header="Size" header-class="bg-light text-dark" :style="{ width: '100px' }">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <div v-else class="cursor-pointer" @click="handleItemClick(row.data)">
                <span v-if="row.data.type" >{{ ['folder','link'].includes(row.data.type) ? "-" : (row.data.file_size ? row.data.file_size + ' kb' : '-') }}</span>
              </div>
            </template>
          </Column>
          <Column v-if="usedIn === 'SmartcatAttachment'" header="Size" header-class="bg-light text-dark">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <div v-else class="cursor-pointer" @click="handleItemClick(row.data)">
                <span v-if="row.data.type" >{{ ['folder','link'].includes(row.data.type) ? "" : (row.data.file_size ? row.data.file_size + ' kb' : '-') }}</span>
              </div>
            </template>
          </Column>
          <Column v-if="usedIn === 'SmartcatAttachment'" header="Uploaded at" header-class="bg-light text-dark">
            <template #body="row">
              <Skeleton v-if="table.onLoad" />
              <div v-else class="cursor-pointer" @click="handleItemClick(row.data)">
                <span v-if="row.data.type === 'file'">{{ formatDate(row.data.created_at) }}</span>
              </div>
            </template>
          </Column>
          <template #empty>
            <div class="text-center py-4">
              <img src="/no_data.png" alt="No Data" class="no-data-img" />
              <p class="text-muted mt-2">No items found</p>
            </div>
          </template>
        </DataTable>
      </div>
    </div>

    <!-- Dialog Modal Utama -->
    <DialogPrime v-model:visible="modal.visible" :header="modal.title" :style="{width: ['move_item', 'upload_file'].includes(modal.action) ? '45rem' : (modal.action === 'preview_pdf') ? '95vw' : '30rem', maxHeight: modal.action === 'preview_pdf' ? '95vh' : 'auto', margin: '10px', zIndex: 1000 }" :pt="modal.action === 'preview_pdf' ? { mask: { style: 'backdrop-filter: blur(5px); background: rgba(0,0,0,0.3);' }, content: { style: 'background: transparent; border: none; box-shadow: none;' }, header: { style: 'background: rgba(255,255,255,0.95); backdrop-filter: blur(10px); border-radius: 12px 12px 0 0;' }, closeButton: { style: 'background: rgba(255,255,255,0.95); backdrop-filter: blur(10px);' } } : {}">
      
      <div v-if="modal.action === 'upload_file' && modal.onLoading && modal.uploadProgress > 0" class="mb-3">
        <div class="progress rounded" style="height: 1.5rem;">
          <div class="progress-bar bg-primary rounded" role="progressbar" :style="{ width: modal.uploadProgress + '%', transition: 'width 0.3s ease' }" :aria-valuenow="modal.uploadProgress" aria-valuemin="0" aria-valuemax="100">
            {{ modal.uploadProgress }}%
          </div>
        </div>
      </div>
      
      <!-- Preview PDF (Restored) -->
      <div v-if="modal.action === 'preview_pdf'">
        <iframe v-if="modal.data.pdf_url" :src="modal.data.pdf_url + '#zoom=150'" class="pdf-iframe"></iframe>
      </div>

      <div v-if="modal.action === 'add_folder'">
        <div class="mb-5">
          <label for="folder_name_modal" class="font-semibold">Folder Name <span class="text-danger">*</span></label>
          <InputText id="folder_name_modal" v-model="modal.data.folder_name" placeholder="Eg: Bearing" :class="`${(modal.errorMessage['folder_name'] && modal.data.folder_name == '') ? 'border-danger' : ''}`" fluid />
          <small v-if="modal.errorMessage['folder_name'] && modal.data.folder_name == ''" class="text-danger">{{ modal.errorMessage['folder_name'] }}</small>
        </div>
      </div>
      <div v-if="modal.action === 'upload_file'">
        <div class="mb-5">
          <label for="files_modal" class="font-semibold">Files <span class="text-danger">*</span></label>
          <UploadMultFilesWithPrimeVue :files="modal.data.files" :is-multiple="true" :max-size="2097152" @update-files="modal.data.files = $event" />
        </div>
      </div>
      <div v-if="modal.action === 'upload_link'">
        <div class="mb-5">
          <label for="files_link_modal" class="font-semibold">Link <span class="text-danger">*</span></label>
          <input id="files_link_modal" v-model="modal.data.files" class="form-control" :class="`${(modal.errorMessage['files'] && modal.data.files === '') ? 'border-danger' : ''}`" placeholder="exp: https://www.youtube.com/"/>
          <small v-if="modal.errorMessage['files'] && modal.data.files === ''" class="text-danger">{{ modal.errorMessage['files'] }}</small>
        </div>
      </div>
      <div v-if="modal.action === 'rename_item'">
        <div v-if="modal.data.type === 'folder'" class="mb-5">
          <label for="folder_name_rename_modal" class="font-semibold">Rename Folder <span class="text-danger">*</span></label>
          <InputText id="folder_name_rename_modal" v-model="modal.data.folder_name_rename" placeholder="Eg: Bearing" :class="`${(modal.errorMessage['folder_name_rename'] && modal.data.folder_name_rename == '') ? 'border-danger' : ''}`" fluid />
          <small v-if="modal.errorMessage['folder_name_rename'] && modal.data.folder_name_rename == ''" class="text-danger">{{ modal.errorMessage['folder_name_rename'] }}</small>
        </div>
        <div v-if="['file', 'link'].includes(modal.data.type)" class="mb-5">
          <label for="file_name_rename_modal" class="font-semibold">Rename File <span class="text-danger">*</span></label>
          <InputText id="file_name_rename_modal" v-model="modal.data.file_name_rename" placeholder="Eg: Bearing" :class="`${(modal.errorMessage['file_name_rename'] && modal.data.file_name_rename == '') ? 'border-danger' : ''}`" fluid />
          <small v-if="modal.errorMessage['file_name_rename'] && modal.data.file_name_rename == ''" class="text-danger">{{ modal.errorMessage['file_name_rename'] }}</small>
        </div>
      </div>
      <div v-if="modal.action === 'move_item'">
        <div class="mb-3 d-flex justify-content-between align-items-center">
          <PrimeBreadcrumb :home="tableModal.home" :model="tableModal.items" class="py-0 px-1" style="font-size: 15px">
            <template #item="{ item, props }">
                <a v-if="item.id && item.folder_name" href="#" v-bind="props.action" @click.prevent="clickCrumbModal('child', item)">
                  <span :class="[item.icon, 'text-color']" />
                  <span class="dark:text-surface-0 font-semibold">{{ item.folder_name }}</span>
                </a>
                <a v-else href="#" v-bind="props.action" @click.prevent="clickCrumbModal('parent', item)">
                    <span class="text-surface-700 dark:text-surface-0">{{ item.folder_name }}</span>
                </a>
            </template>
          </PrimeBreadcrumb>
          <ButtonPrime icon="pi pi-folder-plus" label="Add Folder" size="small" @click="openAddFolderInMoveModal" />
        </div>
        <div class="mb-5">
          <div class="modal-table-container">
            <DataTable :value="tableModal.onLoad ? tableModal.dummyRows : tableModal.data" removable-sort scrollable scroll-height="flex" table-style="min-width: 40rem">
              <Column>
                <template #body="row">
                  <Skeleton v-if="table.onLoad" />
                  <div v-else class="cursor-pointer" @click="handleItemMoveToModal(row.data)">
                    <i v-if="row.data.type" :class="row.data.type === 'folder' ? 'pi pi-folder text-warning' : 'pi pi-file text-primary'"></i>
                    <span class="ms-4">
                      {{ row.data.type === 'folder' ? row.data.folder_name : row.data.file_name }}
                    </span>
                  </div>
                </template>
              </Column>
              <Column :body-style="{ width: '30px', padding: '0' }">
                <template #body="row">
                  <Skeleton v-if="table.onLoad" />
                  <div v-else class="cursor-pointer" @click="handleItemMoveToModal(row.data)">
                    <span v-if="row.data.type" class="pi pi-chevron-right" />
                  </div>
                </template>
              </Column>
              <template #empty>
                <div class="text-center py-4">
                  <img src="/no_data.png" alt="No Data" class="no-data-img" />
                  <p class="text-muted mt-2">No items found</p>
                </div>
              </template>
            </DataTable>
          </div>
        </div>
      </div>
      <div v-if="modal.action === 'copy_link'">
        <div class="mt-3 mb-5">
          <InputGroup>
            <InputText id="copy_link_modal" v-model="modal.data.copy_link" placeholder="Link"/>
            <InputGroupAddon>
              <ButtonPrime v-tooltip.top="'Copy to clipboard'" icon="pi pi-copy" severity="info" :disabled="!modal.data.copy_link" outlined @click="copyToClipboard" />
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>

      <div v-if="!['copy_link', 'preview_pdf'].includes(modal.action)" class="d-flex justify-content-end gap-2">
        <ButtonPrime type="button" label="Cancel" severity="secondary" :disabled="modal.onLoading" @click="modal.visible = false"></ButtonPrime>
        <ButtonPrime v-if="canSaveModal" type="button" :disabled="modal.onLoading ||isDisableDueEmptyUploadFile" @click="saveModal()">
          <span v-if="modal.onLoading"><i class="fas fa-spinner fa-spin"></i>&nbsp;&nbsp;&nbsp;Please wait</span>
          <span v-else>{{ ["add_folder","upload_link"].includes(modal.action) ? "Save" : (modal.action === "upload_file") ? "Upload" : (modal.action === "rename_item") ? "Rename" : (modal.action === "move_item") ? "Move here" : "" }}</span>
        </ButtonPrime>
      </div>
    </DialogPrime>

    <!-- Native Image Preview Trigger (Hidden) -->
    <div style="position: absolute; width: 0; height: 0; overflow: hidden;">
      <PrimeImage ref="imagePreviewer" :src="table.tempImageUrl" preview @load="onPreviewImageLoad" />
    </div>

    <!-- Modal Add Folder in Move Modal -->
    <DialogPrime v-model:visible="modalAddFolderInMove.visible" header="Add Folder" :style="{width: '30rem'}">
      <div class="mb-3">
        <label for="new_folder_name" class="font-semibold">Folder Name <span class="text-danger">*</span></label>
        <InputText id="new_folder_name" v-model="modalAddFolderInMove.folderName" placeholder="Enter folder name" fluid />
      </div>
      <div class="d-flex justify-content-end gap-2">
        <ButtonPrime type="button" label="Cancel" severity="secondary" @click="modalAddFolderInMove.visible = false"></ButtonPrime>
        <ButtonPrime v-if="hasAnyPermission('smartcat-attachment-create-folder')" type="button" label="Create" @click="createFolderInMoveModal"></ButtonPrime>
      </div>
    </DialogPrime>
  </div>
  <NoAccess v-else-if="usedIn === 'SmartcatAttachment'" />
  <div v-else class="text-center text-muted py-4">
    <i class="pi pi-lock me-2"></i>You do not have permission to access attachments.
  </div>
</template>

<script>
import { mapState } from "vuex";
import NoAccess from "@/components/NoAccess.vue";
import smartcatAttachmentService from "@/services/dictionary/smartcatAttachmentService"
import api from "@/helpers/api";
import baseApiUrl from "@/configs/config";
import moment from "moment";

export default {
	name: 'SmartCatAttachment',
  props: {
    usedIn: {
      type: String,
      default: 'SmartcatAttachment',
      required: true
    }
  },
  components: {
    NoAccess,
  },
  data() {
    return {
      breadCrmb: {
        home: { folder_name: "My Attachments", route: "/dictionary/smartcat-attachment" },
        items: [],
      },
      table: {
        onLoad: true,
        total: 0,
        page: {
          current_page: 1,
          total_page: 1,
        },
        limit: 100,
        search: this.$route.query.search || "",
        data: [{}],
        activeIdForDisplayFile: 0,
        dummyRows: Array.from({ length: 10 }, () => ({})),
        tempImageUrl: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7",
        loadTimeout: null,
      },
      tableModal: {
        onLoad: false,
        total: 0,
        page: {
          current_page: 1,
          total_page: 1,
        },
        limit: 100,
        data: [{}],
        dummyRows: Array.from({ length: 10 }, () => ({})),
        home: { folder_name: "Main Folder" },
        items: [],
      },
      modal: {
        visible: false,
        action: '',
        title: '',
        data: {},
        errorMessage: {},
        onLoading: false,
        uploadProgress: 0,
      },
      modalAddFolderInMove: {
        visible: false,
        folderName: '',
      },
      folderStack: {}, // untuk navigasi folder in current time
			apiUrl: baseApiUrl.apiUrl,
    };
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    isDisableDueEmptyUploadFile() {
      if (Object.keys(this.modal.data).length === 0) return true
      return this.modal.action === 'upload_file' &&
      (!this.modal.data.files || this.modal.data.files.length === 0)
    },
    canSaveModal() {
      const action = this.modal.action;
      if (action === 'add_folder') return this.hasAnyPermission('smartcat-attachment-create-folder');
      if (['upload_file', 'upload_link'].includes(action)) return this.hasAnyPermission('smartcat-attachment-create-file');
      if (action === 'rename_item') return this.canAction('rename', this.modal.data.type);
      if (action === 'move_item') return this.canAction('moveto', this.modal.data.type);
      return false;
    }
  },
  watch: {
    'modal.visible'(val) {
      if (!val) {
        if (this.modal.action === 'preview_pdf' && this.modal.data.pdf_url) {
          URL.revokeObjectURL(this.modal.data.pdf_url);
        }
      }
    }
  },
  mounted() {
    if (this.hasAnyPermission('smartcat-attachment-view')) {
      this.getAll();
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    canAction(action, type) {
      const suffix = type === 'folder' ? 'folder' : 'file';
      return this.hasAnyPermission(`smartcat-attachment-${action}-${suffix}`);
    },
    clickCrumb(value, item){
      this.table.search = "";
      value === 'child' ? this.getDetail(item, 'breadCrumb') : this.getAll();
    },
    clickCrumbModal(value, item){
      this.tableModal.search = "";
      value === 'child' ? this.getDetailModal(item, 'breadCrumb') : this.getAllModal();
      this.modal.data.item_to_id = item.id
    },
    filter() {
      clearTimeout(this.table.loadTimeout);
      if (this.usedIn === 'CataloguerForm') {
        this.table.loadTimeout = setTimeout(() => {
          if (this.folderStack && Object.keys(this.folderStack).length > 0) {
            this.getDetail(this.folderStack, "search")
          } else {
            this.getAll()
          }
        }, 500)
      } else {
        if (this.table.search != this.$route.query.search) {
          this.table.loadTimeout = setTimeout(() => {
            if (this.table.search.length > 3) {
              this.table.search = this.table.search.toLowerCase()
              this.$router.push({ name: 'Smartcat Attachment', params: { moduleSlug: 'material-management' }, query: { search: this.table.search } });
            } else {
              this.$router.push({ name: 'Smartcat Attachment', params: { moduleSlug: 'material-management' } });
            }

            if (this.folderStack && Object.keys(this.folderStack).length > 0) {
              this.getDetail(this.folderStack, "search")
            } else {
              this.getAll()
            }
          }, 500)
        }
      }
    },
    async getAll() {
      this.table.onLoad = true
      try {
        const response = await api(this, smartcatAttachmentService.listAll({search: this.table.search }), false);
        this.table.data = response.data;
        this.breadCrmb.items = []
        this.folderStack = {}
      } catch (error) {
        this.table.data = [];
        this.handleErrorObjectNested(error)
      } finally {
        this.table.onLoad = false
      }
    },
    async getDetail(item, clickIn) {
      const {folder_name, id} = item
      this.table.onLoad = true
      try {
        const respDet = await api(this, smartcatAttachmentService.detailFolder(id, {search:this.table.search }), false)
        this.table.data = respDet.data.attachment || [];
      } catch (error) {
        this.table.data = []
        this.handleErrorObjectNested(error)
      } finally {
        const foundBreadCrm = this.breadCrmb.items.findIndex(i => i.id === id)
        if (foundBreadCrm === -1) {
          this.breadCrmb.items.push({ folder_name: folder_name, id: id, row: item.row || item })
        } else {
          this.breadCrmb.items.splice(foundBreadCrm + 1)
        }
        if (clickIn === 'breadCrumb') this.folderStack = (item.row);
        this.table.onLoad = false
      }
    },
    async handleItemClick(item) {
      if (this.usedIn === 'CataloguerForm' && (item.type === 'file' || item.type === 'link')) {
        const fileData = { ...item };
        this.$emit('file-selected', fileData);
        return;
      }
      
      if (item.type === 'folder') {
        this.table.search = "";
        this.folderStack = (item);
        this.getDetail(item, "row");
      } else if (item.type === 'file') {
        const ext = this.getFileExtension(item.file_name);
        if (this.isImage(item.file_name)) {
          try {
            const response = await smartcatAttachmentService.showFile(item.id);
            // Revoke URL lama jika ada
            if (this.table.tempImageUrl && this.table.tempImageUrl.startsWith('blob:')) {
               URL.revokeObjectURL(this.table.tempImageUrl);
            }
            this.table.tempImageUrl = URL.createObjectURL(response.data);
            // Preview akan di-trigger otomatis via onPreviewImageLoad saat src berubah & load selesai
          } catch (e) {
            this.$toastNotifPrime('error', 'Error', 'Failed to load Image');
          }
        } else if (ext === 'pdf') {
          try {
            const response = await smartcatAttachmentService.showFile(item.id);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const pdfUrl = URL.createObjectURL(blob);
            
            this.modal = {
              visible: true,
              action: 'preview_pdf',
              title: item.file_name,
              data: { file_id: item.id, pdf_url: pdfUrl },
              errorMessage: {},
              onLoading: false,
            };
          } catch (e) {
            this.$toastNotifPrime('error', 'Error', 'Failed to load PDF');
          }
        } else {
          this.downloadFile(item);
        }
      } else if (item.type === 'link') {
        window.open(item.file_name, '_blank');
      }
    },
    onPreviewImageLoad() {
      // Pastikan bukan placeholder transparan yang memicu preview
      if (this.table.tempImageUrl && this.table.tempImageUrl.startsWith('blob:')) {
        this.$nextTick(() => {
          if (this.$refs.imagePreviewer && typeof this.$refs.imagePreviewer.onImageClick === 'function') {
            this.$refs.imagePreviewer.onImageClick();
          }
        });
      }
    },
    downloadFile(file) {
      const fileName = file.file_name.toLowerCase();
      smartcatAttachmentService.showFile(file.id).then(response => {
        const fileBlob = new Blob([response.data]);
        const fileURL = URL.createObjectURL(fileBlob);
        const link = document.createElement('a');
        link.href = fileURL;
        link.download = fileName;
        link.click();
        setTimeout(() => { URL.revokeObjectURL(fileURL); }, 5000);
      })
      .catch(error => { this.handleErrorObjectNested(error) })
    },
    openModalAddFolder(){
      this.modal = {
        visible: true,
        action: 'add_folder',
        title: 'Add Folder',
        data: { folder_name: "", parent_code: "" },
        errorMessage: {},
        onLoading: false,
      }
    },
    openModalUploadFile() {
      this.modal = {
        visible: true,
        action: 'upload_file',
        title: 'Upload File',
        data: { folder_attachment_id: "", type:"file", files: [] },
        errorMessage: {},
        onLoading: false,
      }
    },
    openModalUploadLink() {
      this.modal = {
        visible: true,
        action: 'upload_link',
        title: 'Upload Link',
        data: { folder_attachment_id: "", type:"link", files: "" },
        errorMessage: {},
        onLoading: false,
      }
    },
    openModalRenameItem(data) {
      this.modal = {
        visible: true,
        action: 'rename_item',
        title: 'Rename Item',
        data: { ...data, folder_name_rename: data?.folder_name || "", file_name_rename: data && data.file_name && data.type !== 'link' ? data.file_name.substring(0, data.file_name.lastIndexOf('.')) : data?.file_name || "" },
        errorMessage: {},
        onLoading: false,
      }
    },
    openModalMoveItem(data) {
      this.modal = {
        visible: true,
        action: 'move_item',
        title: 'Move Item',
        data: { ...data, file_type: data.type === 'link' ? 'file' : data.type , item_id: data.id, item_to_id: "" },
        errorMessage: {},
        onLoading: false,
      }
      if (this.folderStack && Object.keys(this.folderStack).length > 0) {
        this.getDetailModal(this.folderStack);
      } else {
        this.getAllModal();
      }
    },
    openModalCopyLink(data){
      this.modal = {
        visible: true,
        action: 'copy_link',
        title: 'Copy Link',
        data: { 
          ...data, 
          file_type: data.type === 'link' ? 'file' : data.type , 
          item_id: data.id, 
          copy_link: data.type === "file" ? `${this.apiUrl}/dictionary/smartcat-attachment/file/${data.id}` : (data.type === "link") ? data.original_filename : "" },
        errorMessage: {},
        onLoading: false,
      }
    },
    async copyToClipboard() {
      try {
        await navigator.clipboard.writeText(this.modal.data.copy_link)
        this.$toastNotifPrime("success", "Copied", "Link copied to clipboard");
      } catch (err) {
        this.$toastNotifPrime("error", "Error Copied", "Failed to copy link");
      }
    },
    async getAllModal() {
      this.tableModal.onLoad = true
      try {
        const response = await api(this, smartcatAttachmentService.listAll(), false);
        this.tableModal.data = response.data.filter(i => i.type !== "file" && i.type !== "link");
        this.tableModal.items = []
      } catch (error) {
        this.tableModal.data = [];
        this.handleErrorObjectNested(error)
      } finally {
        this.tableModal.onLoad = false
      }
    },
    async getDetailModal(item) {
      const {folder_name, id} = item
      this.tableModal.onLoad = true
      try {
        const respDet = await api(this, smartcatAttachmentService.detailFolder(id), false)
        this.tableModal.data = respDet.data.attachment.filter(i => i.type !== "file" && i.type !== "link");
      } catch (error) {
        this.tableModal.data = []
        this.handleErrorObjectNested(error)
      } finally {
        const foundBreadCrmModal = this.tableModal.items.findIndex(i => i.id === id)
        if (foundBreadCrmModal === -1) {
          this.tableModal.items.push({ folder_name: folder_name, id: id, row: item.row || item })
        } else {
          this.tableModal.items.splice(foundBreadCrmModal + 1)
        }
        this.tableModal.onLoad = false
      }
    },
    handleItemMoveToModal(data) {
      this.modal.data.item_to_id = data.id
      this.getDetailModal(data)
    },
    openAddFolderInMoveModal() {
      this.modalAddFolderInMove.visible = true;
      this.modalAddFolderInMove.folderName = '';
    },
    async createFolderInMoveModal() {
      if (!this.modalAddFolderInMove.folderName) {
        this.$toastNotifPrime("error", "Validation Failed", "Folder name is required");
        return;
      }
      try {
        const currentFolderId = this.tableModal.items.length > 0 ? this.tableModal.items[this.tableModal.items.length - 1].id : "0";
        await api(this, smartcatAttachmentService.createFolder({ folder_name: this.modalAddFolderInMove.folderName, parent_code: currentFolderId }));
        this.$toastNotifPrime("success", "Success", "Folder created successfully");
        this.modalAddFolderInMove.visible = false;
        if (this.tableModal.items.length > 0) {
          this.getDetailModal(this.tableModal.items[this.tableModal.items.length - 1]);
        } else {
          this.getAllModal();
        }
      } catch (error) { this.handleErrorObjectNested(error); }
    },
    saveModal() {
      let isValid = true;
      let payload = {};
      const stackId = this.folderStack?.id || "0";
      const files = this.modal.data.files ? this.modal.data.files : (this.modal.action === 'upload_file') ? [] : "";
      const formData = new FormData();
      const setError = (key, msg) => { this.modal.errorMessage[key] = msg; isValid = false; };
      const actions = {
        add_folder: () => {
          if (!this.modal.data.folder_name) setError("folder_name", "Folder Name is required");
          payload = { folder_name: this.modal.data.folder_name, parent_code: stackId };
        },
        upload_file: () => {
          if (!files.length) setError("files", "Files is required");
          files.forEach(f => { formData.append("files[]", f) });
          if (this.folderStack && Object.keys(this.folderStack).length > 0) formData.append("folder_attachment_id", stackId);
          formData.append("type", "file")
        },
        upload_link: () => {
          if (!files) setError("files", "Link is required");
          payload = { type: "link", file: files };
          if (this.folderStack && Object.keys(this.folderStack).length > 0) payload.folder_attachment_id = stackId
        },
        rename_item: () => {
          const isFolder = this.modal.data.type === "folder";
          const isFile = this.modal.data.type === "file";
          const key = isFolder ? "folder_name_rename" : "file_name_rename";
          const label = isFolder ? "Folder Name" : "File Name";
          if (!this.modal.data[key]) setError(key, `${label} is required`);
          let newName = this.modal.data[key];
          if (isFile && this.modal.data.file_name) {
            const ext = this.getFileExtension(this.modal.data.file_name);
            if (ext && newName && !newName.toLowerCase().endsWith(`.${ext}`)) {
              newName = `${newName}.${ext}`;
            }
          }
          payload = { [isFolder ? "folder_name" : (isFile) ? "file_name" : "file"]: newName, item_will_be_renamed: this.modal.data.id, type: this.modal.data.type };
        },
        move_item: () => {
          payload = { item_will_be_moved: this.modal.data.item_id, file_type: this.modal.data.file_type, folder_id: this.modal.data.item_to_id || "0" };
        }
      };
      actions[this.modal.action]?.();
      if (!isValid) {
        this.$toastNotifPrime("error", "Validation Failed", "There field is empty");
        return false;
      }
      this.modal.onLoading = true
      setTimeout(async() => {
        try {
          if (this.modal.action === 'add_folder') {
            await api(this, smartcatAttachmentService.createFolder(payload));
          } else if (['upload_file', 'upload_link'].includes(this.modal.action)) {
            const condPayload = this.modal.action === 'upload_file' ? formData : payload
            if (this.modal.action === 'upload_file') {
              this.modal.uploadProgress = 0;
              let lastUpdate = 0;
              await api(this, smartcatAttachmentService.uploadFile(condPayload, {
                onUploadProgress: (progressEvent) => {
                  const now = Date.now();
                  if (now - lastUpdate > 100) {
                    this.modal.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    lastUpdate = now;
                  }
                }
              }));
            } else { await api(this, smartcatAttachmentService.uploadFile(condPayload)); }
          } else if (this.modal.action === 'rename_item') {
            const srvcFunct = (this.modal.data.type === "folder") ? smartcatAttachmentService.renameFolder(payload.item_will_be_renamed, payload) : smartcatAttachmentService.renameFile(payload.item_will_be_renamed, payload)
            await api(this, srvcFunct);
          } else if (this.modal.action === 'move_item') {
            await api(this, smartcatAttachmentService.moveFileOrFolder(payload.item_will_be_moved, payload))
          }
          if (this.folderStack && Object.keys(this.folderStack).length > 0) { await this.getDetail(this.folderStack, "save") } else { await this.getAll() }
          await new Promise(resolve => setTimeout(resolve, 1500));
          this.modal.data = {}; this.modal.visible = false; this.modal.uploadProgress = 0;
        } catch (error) { this.handleErrorObjectNested(error) } finally { this.modal.onLoading = false }
      }, 500)
    },
    deleteItem(item) {
      this.confirmDialog('Are you sure you want to delete this item?', "delete it").then((result) => {
        if (result.isConfirmed) {
          const srvc = (item.type === 'folder') ? smartcatAttachmentService.deleteFolder(item.id) : smartcatAttachmentService.deleteFile(item.id)
          this.table.onLoad = true
          api(this, srvc).then(() => {
            if (this.folderStack && Object.keys(this.folderStack).length > 0) { this.getDetail(this.folderStack, "delete") } else { this.getAll() }
          }).catch(err => { this.handleErrorObjectNested(err); this.table.onLoad = false; })
        }
      });
    },
    getFileExtension(filename) { return filename.split('.').pop().toLowerCase(); },
		getFileIcon(filename) {
			const ext = this.getFileExtension(filename);
			switch (ext) {
				case 'pdf': return 'pi pi-file-pdf text-danger';
				case 'doc':
				case 'docx': return 'pi pi-file-word text-primary';
				case 'xls':
				case 'xlsx': return 'pi pi-file-excel text-success';
				case 'ppt':
				case 'pptx': return 'pi pi-file-ppt text-danger';
				case 'txt': return 'pi pi-file text-secondary';
				default: return 'pi pi-file text-primary';
			}
		},
    isImage(filename) {
      if (!filename) return false;
      const ext = this.getFileExtension(filename);
      return ['png', 'jpeg', 'jpg', 'gif', 'webp'].includes(ext);
    },
    formatDate(date) { return date ? moment(date).format("DD MMM YYYY, HH:mm") : "-"; },
    shortenLink(text, maxLength = 50) {
      if (!text) return "";
      return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    },
    handleErrorObjectNested(error) {
      if (error.response?.data?.code == 400 || error.response?.status == 422 || error.response?.status == 500) {
        const errorData = error.response?.data?.data;
        if (typeof errorData === 'object' && errorData !== null) {
          for(const key in errorData) {    
            if (Object.prototype.hasOwnProperty.call(errorData, key)) {
              const match = key.match(/^items\.(\d+)\.(.+)$/);
              if (match) {
                error.response.data.data[key].forEach(message => { this.$toastNotifPrime('error', error.response.data.message, `${message} in row ${parseInt(match[1]) + 1}`) })
              } else { this.$toastNotifPrime('error', error.response.data.message, errorData[key]); }                
            } 
          }
        } 
      }
    },
  },
};
</script>

<style scoped>
  .action-buttons-hover { opacity: 0; transition: opacity 0.2s; }
  .item-row:hover .action-buttons-hover { opacity: 1; }
  .choosable-item { cursor: pointer; }
  ::v-deep(.choosable-item:hover td) { background-color: rgba(59, 130, 246, 0.1) !important; }
  ::v-deep(.p-datatable-sm.text-xs tbody td) { padding-left: 0.75rem !important; }
  ::v-deep(.p-datatable-sm.text-xs .action-buttons-hover .p-button) { width: 1.5rem !important; height: 1.5rem !important; }
  ::v-deep(.p-datatable-sm.text-xs .action-buttons-hover .p-button .p-button-icon) { font-size: 0.65rem !important; }
  ::v-deep(.text-xs .p-breadcrumb .p-breadcrumb-separator) { font-size: 0.6rem !important; }
  .table-container { height: 450px; overflow-y: auto; }
  .modal-table-container { height: 300px; overflow-y: auto; }
  .no-data-img { width: 100px; height: 100px; opacity: 0.5; }
  .pdf-iframe { width: 100%; height: 80vh; border: none; border-radius: 0 0 12px 12px; }
</style>
